export const TIMELINE_ZOOM_STEP = 0.3;
export const TIMELINE_PAN_FRACTION = 0.35;
export const VIS_TIMELINE_DEFAULT_ZOOM_MIN_MS = 10;

export type TimelinePanDirection = 'left' | 'right';
export type TimelineZoomDirection = 'in' | 'out';

export interface TimelineWindowRange {
  start: Date;
  end: Date;
}

export interface TimelineNavigationBounds {
  min: Date;
  max: Date;
  zoomMinMs?: number;
}

export interface TimelineNavigationApi {
  destroy: () => void;
  setOptions: (opts: { height?: string }) => void;
  setWindow: (start: Date, end: Date) => void;
  getWindow: () => TimelineWindowRange;
  getItemRange: () => { min: Date; max: Date };
  zoomIn: (percentage: number) => void;
  zoomOut: (percentage: number) => void;
  fit: () => void;
}

export interface TimelineNavigationAvailability {
  panLeft: boolean;
  panRight: boolean;
  zoomIn: boolean;
  zoomOut: boolean;
  fit: boolean;
}

const UNAVAILABLE_NAVIGATION: TimelineNavigationAvailability = {
  panLeft: false,
  panRight: false,
  zoomIn: false,
  zoomOut: false,
  fit: false,
};

export function shiftTimelineWindow(
  window: TimelineWindowRange,
  direction: TimelinePanDirection,
  fraction: number = TIMELINE_PAN_FRACTION
): TimelineWindowRange {
  const delta = (window.end.getTime() - window.start.getTime()) * fraction;
  const signedDelta = direction === 'right' ? delta : -delta;
  return {
    start: new Date(window.start.getTime() + signedDelta),
    end: new Date(window.end.getTime() + signedDelta),
  };
}

export function zoomedTimelineWindow(
  window: TimelineWindowRange,
  direction: TimelineZoomDirection,
  percentage: number = TIMELINE_ZOOM_STEP
): TimelineWindowRange {
  const start = window.start.getTime();
  const end = window.end.getTime();
  const interval = end - start;
  if (direction === 'in') {
    const newInterval = interval / (1 + percentage);
    const distance = (interval - newInterval) / 2;
    return { start: new Date(start + distance), end: new Date(end - distance) };
  }
  return {
    start: new Date(start - (interval * percentage) / 2),
    end: new Date(end + (interval * percentage) / 2),
  };
}

export function applyTimelineRangeConstraints(
  proposed: TimelineWindowRange,
  bounds: TimelineNavigationBounds,
  current: TimelineWindowRange
): TimelineWindowRange {
  let newStart = proposed.start.getTime();
  let newEnd = proposed.end.getTime();
  const min = bounds.min.getTime();
  const max = bounds.max.getTime();
  const zoomMin = bounds.zoomMinMs ?? VIS_TIMELINE_DEFAULT_ZOOM_MIN_MS;

  if (newEnd < newStart) {
    newEnd = newStart;
  }

  if (newStart < min) {
    const diff = min - newStart;
    newStart += diff;
    newEnd += diff;
    if (newEnd > max) {
      newEnd = max;
    }
  }

  if (newEnd > max) {
    const diff = newEnd - max;
    newStart -= diff;
    newEnd -= diff;
    if (newStart < min) {
      newStart = min;
    }
  }

  if (newEnd - newStart < zoomMin) {
    const currentStart = current.start.getTime();
    const currentEnd = current.end.getTime();
    if (
      currentEnd - currentStart === zoomMin &&
      newStart >= currentStart - 0.5 &&
      newEnd <= currentEnd
    ) {
      newStart = currentStart;
      newEnd = currentEnd;
    } else {
      const diff = zoomMin - (newEnd - newStart);
      newStart -= diff / 2;
      newEnd += diff / 2;
    }
  }

  return { start: new Date(newStart), end: new Date(newEnd) };
}

export function timelineWindowsEffectivelyEqual(
  a: TimelineWindowRange,
  b: TimelineWindowRange
): boolean {
  const interval = Math.max(
    a.end.getTime() - a.start.getTime(),
    b.end.getTime() - b.start.getTime(),
    1
  );
  const epsilon = Math.max(1, interval * 0.001);
  return (
    Math.abs(a.start.getTime() - b.start.getTime()) <= epsilon &&
    Math.abs(a.end.getTime() - b.end.getTime()) <= epsilon
  );
}

function wouldChangeVisibleWindow(
  current: TimelineWindowRange,
  proposed: TimelineWindowRange,
  bounds: TimelineNavigationBounds
): boolean {
  const applied = applyTimelineRangeConstraints(proposed, bounds, current);
  return !timelineWindowsEffectivelyEqual(applied, current);
}

export function getTimelineNavigationAvailability(input: {
  current: TimelineWindowRange;
  bounds: TimelineNavigationBounds;
  itemRange: TimelineWindowRange | null;
}): TimelineNavigationAvailability {
  const { current, bounds, itemRange } = input;
  return {
    panLeft: wouldChangeVisibleWindow(current, shiftTimelineWindow(current, 'left'), bounds),
    panRight: wouldChangeVisibleWindow(current, shiftTimelineWindow(current, 'right'), bounds),
    zoomIn: wouldChangeVisibleWindow(current, zoomedTimelineWindow(current, 'in'), bounds),
    zoomOut: wouldChangeVisibleWindow(current, zoomedTimelineWindow(current, 'out'), bounds),
    fit: itemRange ? wouldChangeVisibleWindow(current, itemRange, bounds) : false,
  };
}

export function unavailableTimelineNavigation(): TimelineNavigationAvailability {
  return UNAVAILABLE_NAVIGATION;
}
